<template>
    <v-dialog v-model="dialogOpen" max-width="600" :persistent="form.processing">
      <v-card :loading="invoiceLoading" :disabled="invoiceLoading || form.processing">
        <v-card-title>{{ dialogTitle }}</v-card-title>
        <v-card-text>
            <div class="d-flex flex-column ga-4">
                <div class="well mt-2 fw-medium" v-if="props.companyId || !creating">
                    <div v-if="company">Company: {{ company?.name }}</div>
                    <div v-else>Company: {{ companies?.[0]?.name }}</div>
                </div>
                <div class="well d-flex flex-column ga-6">
                    <div :class="{'d-none': props.companyId || !creating}">
                        <v-select-v1
                            label="Company"
                            v-model="form.company"
                            :items="companies"
                            item-title="name"
                            item-value="id"
                            :error-messages="form.errors.company"
                        >
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props" :title="null">
                                    <div class="fs-n1">{{ item.raw.name }}</div>
                                </v-list-item>
                            </template>
                        </v-select-v1>
                    </div>
                    <div>
                        <v-number-input-v1
                            label="Amount"
                            placeholder="0.00"
                            :min="0"
                            v-model="form.amount"
                            @change="(e: any) => {form.amount = helpers.numberFormatter(e.target.value)}"
                            :error-messages="form.errors.amount"
                        >
                            <template #prepend-inner>
                                <mdi-icon>payments</mdi-icon>
                            </template>
                            <template #append-inner>
                                USD
                            </template>
                        </v-number-input-v1>
                    </div>
                    <div>
                        <v-switch-v1 v-model="form.is_bluebanc" label="Bluebanc" density="compact"></v-switch-v1>
                    </div>
                    <div v-if="form.errors.custom_error" class="fs-1 pb-2 text-error">
                        {{ form.errors.custom_error }}
                    </div>
                </div>
            </div>
        </v-card-text>
        <v-card-actions-form-modal>
            <v-btn color="btn-secondary" :disabled="form.processing" @click="close">Close</v-btn>
            <v-btn color="primary" @click="manageInvoice" :loading="form.processing" :disabled="form.processing">Save</v-btn>
        </v-card-actions-form-modal>
      </v-card>
    </v-dialog>
  </template>
  
  <script lang="ts" setup>
    import { ref, watch, defineProps, defineEmits } from 'vue';
    import { Page, GlobalEventCallback } from '@inertiajs/core';
    import axios from '@/Plugins/axios';
    import { User } from '@/Types/Models/user';
    import { XhrError } from '@/Types/xhr-error';
    import { useForm } from '@inertiajs/vue3';
    import { Invoice } from '@/Types/Models/invoice';
    import { Company } from '@/Types/Models/company';
    import helpers from '@/Plugins/helpers';
    import { SharedPageProps } from '@/Types/inertia';
    import { toast } from "vue3-toastify";
    
    interface Props {
        modelValue: boolean; // v-model will be linked to this prop
        invoiceId?: number;
        companyId?: number;
    }
    
    const props = defineProps<Props>();
    const emit = defineEmits(['update:modelValue', 'update:invoiceId', 'update:companyId', 'updateInvoiceList']);
    
    
    // Internal copy of the v-model value
    const dialogOpen = ref(props.modelValue);
    const invoice = ref<Invoice | null>(null);
    const company = ref<Company | null>(null);
    const companies = ref<Company[]>([]);

    const invoiceLoading = ref(false);
    const error = ref('');
    const dialogTitle = ref('Invoice');
    const creating = ref(true);
    
    // Watch for changes to the v-model prop and update internal value
    watch(() => props.modelValue, (newValue) => {
        dialogOpen.value = newValue;
        if(dialogOpen.value){
            console.log('props', props)
            loadInvoice();
        }
    });
    
    // Emit changes back to the parent when the internal value changes
    watch(dialogOpen, (newValue) => {
        if(!dialogOpen.value){
            clearDialogData();
        }
        emit('update:modelValue', newValue);
    });
    
    // Close modal
    function close() {
        dialogOpen.value = false;
    }

    const clearDialogData = () => {
        
        invoice.value = null;
        company.value = null;
        companies.value = [];
        error.value = '';
        form.reset();
        form.clearErrors();
        creating.value = true;
        emit('update:invoiceId', 0);
        emit('update:companyId', 0);
    }

    const loadInvoice = () => {
        invoiceLoading.value = true;
        let _route = null;
        if(props.invoiceId){
            _route = route('invoices.manage', props.invoiceId);
        }
        else if(props.companyId){
            _route = route('invoices.create', props.companyId);
        }
        else{
            _route = route('invoices.create');
        }
        axios.get(_route).then(({data}) => {
            console.log('data', data)
            let content = data.data;
            invoice.value = content.invoice;
            company.value = content.company;
            companies.value = content.companies;
            if(invoice.value){
                dialogTitle.value = 'Update Invoice';
                creating.value = false;
                form.amount = helpers.parseFloat(invoice.value.amount);
                form.is_bluebanc = invoice.value.is_bluebanc ?? false;
            }
            else{
                dialogTitle.value = 'Create Invoice';
            }
            if(props.companyId && companies.value?.length){
                form.company = companies.value[0].id;
            }
            else if(company.value){
                form.company = company.value.id;
            }
            
            console.log(props, invoice.value, company.value, companies.value);
        })
        .catch((reason: XhrError) => {
            reason.showErr();
            close();
        })
        .finally(() => {
            invoiceLoading.value = false;
        });
    }

    const form = useForm({
         custom_error: '',
         company: invoice.value?.company_id ?? undefined,
         amount: invoice.value ? helpers.parseFloat(invoice.value.amount) : null,
         is_bluebanc: invoice.value?.is_bluebanc ?? false,
     });

    const manageInvoice = () => {
        form.post(route('invoices.store', invoice.value?.id ?? 0), {
            preserveScroll: true,
            onSuccess: ((page: Page<SharedPageProps>) => {
                form.reset();
                form.clearErrors();
                emit('updateInvoiceList');
                close();
                // toast.success(page.props.jetstream?.flash?.msg?.text ?? 'Invoice saved');
                // setTimeout(() => {
                //     emit('updateInvoiceList');
                //     close();
                // }, 2000);
            }) as GlobalEventCallback<"success">,
        });
    }
</script>